import { Bump } from "@nivo/bump";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Avatar from "@mui/material/Avatar";
import { useQueryTrend } from "../../hooks/top21ApiHooks";
import { useWindowSize } from "../../hooks/windowHooks";
import { useAppStore } from "../../store/appStore";
import { sidebarWidth } from "../Menu";
import Top21TrendLoading from "./Top21TrendLoading";
import { pad } from "../../fn/pad";

const TrendPage = () => {
	const { width, height } = useWindowSize();
	const darkModeEnabled = useAppStore((state) => state.darkModeEnabled);
	const listOption = useAppStore((state) => state.listOption);
	const top21TrendsRangeStart = useAppStore(
		(state) => state.top21TrendsRangeStart
	);
	const top21TrendsRangeEnd = useAppStore((state) => state.top21TrendsRangeEnd);
	const top21TrendsInterval = useAppStore((state) => state.top21TrendsInterval);
	const top21TimeFilter = useAppStore((state) => state.top21TimeFilter);

	const { isLoading, isError, error, data } = useQueryTrend({
		listOption,
		timeRangeMin: top21TrendsRangeStart,
		timeRangeMax: top21TrendsRangeEnd,
		timeInterval: top21TrendsInterval,
		timeFilter: top21TimeFilter,
	});

	if (isLoading || !width || !height) return <Top21TrendLoading />;

	if (isError) return <div>Error found, {error}</div>;

	const ChartTooltip = ({ serie }) => {
		return (
			<Paper sx={{ minWidth: 200 }}>
				<List dense disablePadding>
					<ListSubheader>
						<Stack direction="row" alignItems="center" spacing={2}>
							<ListItemIcon>
								<Avatar
									alt={serie.id}
									src={`${process.env.PUBLIC_URL}/images/${
										listOption === "f" ? "f" : "m"
									}/top21/${parseInt(serie.id.substring(1))}.jpg`}
									sx={{ width: 64, height: 64 }}
								/>
							</ListItemIcon>
							<Typography>{serie.id}</Typography>
						</Stack>

						<ListItemSecondaryAction>
							<Typography>{listOption === "f" ? "🌸" : "🍀"}</Typography>
						</ListItemSecondaryAction>
					</ListSubheader>
				</List>
			</Paper>
		);
	};

	const chartData = data?.records.map((choice) => {
		return {
			id: `#${pad(choice.id, 2)}`,
			data: choice.rankTrends,
		};
	});

	const chartProps = {
		data: chartData,
		height: height - 20,
		width:
			chartData?.length > 0
				? chartData[0].data.length * 90 >
				  (width >= 1080 ? width - sidebarWidth : width)
					? chartData[0].data.length * 90
					: width >= 1080
					? width - sidebarWidth
					: width
				: 0,
		theme: { textColor: darkModeEnabled ? "#FFF" : "#000" },
		colors: { scheme: darkModeEnabled ? "paired" : "dark2" },
		lineWidth: 20,
		activeLineWidth: 20,
		inactiveLineWidth: 6,
		inactiveOpacity: 0.15,
		pointSize: 22,
		activePointSize: 22,
		inactivePointSize: 0,
		pointColor: { theme: "background" },
		pointBorderWidth: 6,
		activePointBorderWidth: 6,
		pointBorderColor: { from: "serie.color" },
		axisTop: {
			tickSize: 15,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
			legendPosition: "middle",
			legendOffset: -36,
		},
		axisBottom: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
			legendPosition: "middle",
			legendOffset: 36,
		},
		axisRight: {
			tickSize: 5,
			tickPadding: 35,
			tickRotation: 0,
			legend: "排名",
			legendPosition: "middle",
			legendOffset: 75,
		},
		axisLeft: null,
		startLabel: true,
		endLabel: true,
		tooltip: ChartTooltip,
		margin: { bottom: 40, top: 40, left: 40, right: 100 },
	};

	return (
		<Box
			width={width >= 1080 ? width - sidebarWidth : width}
			height={height}
			display="flex"
		>
			<Box
				sx={{ overflowX: "scroll", overflowY: "hidden" }}
				width={(width >= 1080 ? width - sidebarWidth : width) - 80}
				height={height - 10}
			>
				<Bump
					{...chartProps}
					axisRight={null}
					width={chartProps.width - 80}
					margin={{ ...chartProps.margin, right: 40 }}
				/>
			</Box>
			<Box width={80} height={height - 10}>
				<Bump
					{...chartProps}
					layers={["axes"]}
					axisBottom={null}
					axisTop={null}
					axisLeft={null}
					width={80}
					margin={{ ...chartProps.margin }}
				/>
			</Box>
		</Box>
	);
};

export default TrendPage;
